import { BRWN_PLUME_TYPE, brwnPlumeLabel, isBrwnPlumeType } from './eggs/brwnPlume'

export type PaletteItem = {
  id: string
  label: string
  /** When false the toolbox row is visible but disabled. */
  enabled: boolean
  /** When true the row is omitted from the toolbox (kept for canned / legacy objects). */
  hidden?: boolean
  /** Requires scenarios.view_easter_eggs — omitted from toolbox without the perm. */
  easterEgg?: boolean
}

export type PaletteCategory = {
  id: string
  label: string
  color: string
  items: PaletteItem[]
}

export const PALETTE: PaletteCategory[] = [
  {
    id: 'hazard',
    label: 'Hazard',
    color: '#FF8C1A',
    items: [
      { id: 'fire', label: 'Fire / Ignition', enabled: true },
      { id: 'explosion', label: 'Explosion', enabled: true },
      { id: 'gas_leak', label: 'Gas Leak', enabled: true },
      { id: 'rupture', label: 'Rupture', enabled: true },
      { id: 'vapor_cloud', label: 'Vapor Cloud', enabled: true },
      { id: 'release_liquids', label: 'Liquid Release', enabled: true },
      // Label resolved at runtime from eggs/brwnPlume (unicode-escaped there).
      { id: BRWN_PLUME_TYPE, label: brwnPlumeLabel(), enabled: true, easterEgg: true },
      { id: 'tornado', label: 'Tornado', enabled: true },
      { id: 'hurricane', label: 'Hurricane', enabled: true },
      { id: 'exclusion', label: 'Release Area', enabled: true, hidden: true },
      // Hidden — still resolve for canned / saved boards
      { id: 'leak', label: 'Leak', enabled: true, hidden: true },
      { id: 'pipeburst', label: 'Pipeburst', enabled: true, hidden: true },
    ],
  },
  {
    id: 'zones',
    label: 'Zones',
    color: '#E24B4A',
    items: [
      { id: 'hot_zone', label: 'Hot Zone', enabled: true },
      { id: 'isolation', label: 'Isolation Areas', enabled: true },
      { id: 'evacuate', label: 'Evacuation', enabled: true },
      { id: 'shelter', label: 'Shelter in Place', enabled: true },
    ],
  },
  {
    id: 'command',
    label: 'Command & Logistics',
    color: '#6FA8E8',
    items: [
      { id: 'icp', label: 'Incident Command Post', enabled: true },
      { id: 'dispatch', label: 'Dispatch', enabled: true },
      { id: 'responder', label: 'Responder', enabled: true },
      { id: 'incident_commander', label: 'Incident Commander', enabled: true },
      { id: 'chief_officer', label: 'Chief Officer', enabled: true },
      { id: 'operator', label: 'Pipeline Operator', enabled: true },
      { id: 'contractor', label: 'Contractor', enabled: true },
      { id: 'person', label: 'Person', enabled: true },
      { id: 'child', label: 'Child', enabled: true },
      { id: 'staging', label: 'Staging Area', enabled: true },
      { id: 'media_staging', label: 'Media Staging', enabled: true },
      { id: 'muster', label: 'Muster Point', enabled: true },
      { id: 'access_point', label: 'Access Point', enabled: true },
      { id: 'pipeline_personnel', label: 'Pipeline Personnel', enabled: true, hidden: true },
    ],
  },
  {
    id: 'vehicles',
    label: 'Vehicles',
    color: '#6FCB9A',
    items: [
      { id: 'car', label: 'Car', enabled: true },
      { id: 'truck', label: 'Heavy Truck', enabled: true },
      { id: 'engine', label: 'Engine Company', enabled: true },
      { id: 'ladder', label: 'Truck / Ladder Company', enabled: true },
      { id: 'ems', label: 'EMS / Ambulance', enabled: true },
      { id: 'hazmat', label: 'HAZMAT', enabled: true },
      { id: 'rescue', label: 'Rescue', enabled: true },
      { id: 'rehab', label: 'Rehab', enabled: true },
      { id: 'police', label: 'Law Enforcement', enabled: true },
      { id: 'work_vehicle', label: 'Work Vehicle', enabled: true },
      { id: 'rollover_vehicle', label: 'Rollover Vehicle', enabled: true },
      { id: 'contractor_response', label: 'Contractor Vehicle', enabled: true },
      { id: 'bulldozer', label: 'Bulldozer', enabled: true },
      { id: 'forestry_bulldozer', label: 'Forestry Bulldozer', enabled: true },
      { id: 'backhoe', label: 'Backhoe', enabled: true },
      { id: 'boat', label: 'Boat', enabled: true },
      { id: 'airplane', label: 'Airplane', enabled: true },
      { id: 'helicopter', label: 'Helicopter', enabled: true },
      { id: 'drone', label: 'Drone', enabled: true },
    ],
  },
  {
    id: 'supplies',
    label: 'Supplies & Resources',
    color: '#5BB89A',
    items: [
      { id: 'air_monitoring', label: 'Air Monitoring', enabled: true },
      { id: 'hard_boom', label: 'Hard Boom', enabled: true, hidden: true },
      { id: 'boom', label: 'Boom', enabled: true },
      { id: 'absorbent_boom', label: 'Absorbent Boom', enabled: true },
      { id: 'foam', label: 'Foam', enabled: true },
      { id: 'absorbent_pads', label: 'Absorbent Pads', enabled: true },
      { id: 'underflow_dam', label: 'Underflow Dam', enabled: true },
      { id: 'overflow_dam', label: 'Overflow Dam', enabled: true },
      { id: 'dike', label: 'Dike', enabled: true },
      { id: 'temporary_mats', label: 'Temporary Mats', enabled: true },
      { id: 'restroom', label: 'Restrooms', enabled: true },
      { id: 'porta_potty', label: 'Porta Potty', enabled: true },
      { id: 'frac_tank', label: 'Frac Tank', enabled: true },
      { id: 'vacuum_tanker', label: 'Tanker', enabled: true },
      { id: 'vac_trucks', label: 'Vacuum Truck', enabled: true },
    ],
  },
  {
    id: 'infra',
    label: 'Infrastructure',
    color: '#C4C0B8',
    items: [
      { id: 'road', label: 'Temporary Road', enabled: true },
      { id: 'valve_open', label: 'Open Valve', enabled: true },
      { id: 'valve_closed', label: 'Closed Valve', enabled: true },
      { id: 'pump', label: 'Pump Station', enabled: true },
      { id: 'compressor', label: 'Compressor', enabled: true },
      { id: 'regulator_station', label: 'Regulator Station', enabled: true },
      { id: 'city_gate', label: 'City Gate', enabled: true },
      { id: 'mercaptan_station', label: 'Mercaptan Station', enabled: true },
      { id: 'meter_station', label: 'Meter Station', enabled: true },
      { id: 'drawbridge', label: 'Drawbridge', enabled: false, hidden: true },
    ],
  },
  {
    id: 'locations',
    label: 'Locations',
    color: '#A89BE8',
    items: [
      { id: 'school', label: 'School', enabled: true },
      { id: 'daycare', label: 'Daycare', enabled: true },
      { id: 'hospital', label: 'Hospital', enabled: true },
      { id: 'water_treatment', label: 'Water Treatment', enabled: true },
    ],
  },
]

export const DRAW_TOOLS: { id: string; label: string; enabled: boolean; hidden?: boolean }[] = [
  { id: 'type', label: 'Type / Label', enabled: true },
  { id: 'freehand', label: 'Freehand Sketch', enabled: true },
  { id: 'pen', label: 'Line', enabled: true },
  { id: 'ruler', label: 'Rule', enabled: true },
]

/**
 * Default hides specialist gear but keeps every category.
 * Simple keeps a few names a layperson already knows. The stored value is still `easy`.
 * Everything shows the full toolbox. Drawing tools are not filtered here.
 */
const DEFAULT_OMIT = new Set([
  'vapor_cloud',
  'exclusion',
  'pipeline_personnel',
  'chief_officer',
  'media_staging',
  'contractor',
  'muster',
  'access_point',
  'forestry_bulldozer',
  'contractor_response',
  'boat',
  'work_vehicle',
  'bulldozer',
  'rehab',
  'underflow_dam',
  'overflow_dam',
  'frac_tank',
  'vac_trucks',
  'temporary_mats',
  'hard_boom',
  'mercaptan_station',
  'city_gate',
  'compressor',
  'meter_station',
  'regulator_station',
])

const EASY_IDS = new Set([
  'fire',
  'gas_leak',
  'evacuate',
  'shelter',
  'icp',
  'dispatch',
  'responder',
  'engine',
  'ems',
  'police',
  'foam',
  'road',
  'valve_open',
  'valve_closed',
  'school',
  'hospital',
])

export function paletteItemVisible(
  complexity: 'easy' | 'default' | 'everything' | undefined,
  itemId: string,
): boolean {
  if (complexity === 'easy') return EASY_IDS.has(itemId)
  if (complexity === 'default') return !DEFAULT_OMIT.has(itemId)
  return true
}

const ITEM_BY_ID = new Map(PALETTE.flatMap((c) => c.items.map((i) => [i.id, { ...i, color: c.color }])))

export function paletteColor(typeId: string): string {
  if (typeId === 'wind') return '#FAC775'
  if (typeId === 'label') return '#E8E4DC'
  if (typeId === 'story_beat') return '#B8D4E8'
  if (typeId === 'sketch') return '#E24B4A'
  if (typeId === 'canal') return '#6FA8E8'
  return ITEM_BY_ID.get(typeId)?.color ?? '#C4C0B8'
}

/** One short line for the palette row. Five words or fewer. */
const PALETTE_HINTS: Record<string, string> = {
  type: 'Text pinned on the map',
  wind: 'Board-wide surface wind',
  freehand: 'Draw a freehand stroke',
  pen: 'Draw a straight segment',
  ruler: 'Measure a map distance',
  fire: 'Mark a fire location',
  explosion: 'Mark an explosion',
  gas_leak: 'Gas leak origin',
  rupture: 'Rupture with a keep-out',
  vapor_cloud: 'Vapor cloud origin',
  release_liquids: 'Liquid release origin',
  tornado: 'Storm with local wind',
  hurricane: 'Sustained wind across miles',
  exclusion: 'Release or exclusion area',
  hot_zone: 'Hot zone keep-out',
  isolation: 'Isolation area',
  evacuate: 'Evacuation zone',
  shelter: 'Shelter-in-place zone',
  icp: 'Incident command post',
  dispatch: 'Dispatch / communications',
  pipeline_personnel: 'Pipeline crew on scene',
  responder: 'Responder on scene',
  incident_commander: 'Incident commander',
  chief_officer: 'Chief officer',
  staging: 'Staging area',
  media_staging: 'Media staging area',
  muster: 'Muster point',
  access_point: 'Access point',
  contractor: 'Contractor on scene',
  operator: 'Pipeline operator on scene',
  person: 'Person on scene',
  child: 'Child on scene',
  car: 'Car on the map',
  truck: 'Heavy truck',
  engine: 'Engine company',
  ladder: 'Ladder company',
  ems: 'EMS or ambulance',
  hazmat: 'HAZMAT unit',
  rescue: 'Rescue unit',
  rehab: 'Rehab unit',
  police: 'Law enforcement unit',
  work_vehicle: 'Work vehicle',
  bulldozer: 'Bulldozer',
  forestry_bulldozer: 'Forestry bulldozer',
  backhoe: 'Backhoe',
  boat: 'Boat',
  airplane: 'Airplane',
  helicopter: 'Helicopter; route with waypoints',
  drone: 'Drone; route with waypoints',
  rollover_vehicle: 'Rollover vehicle',
  contractor_response: 'Contractor vehicle',
  air_monitoring: 'Air monitoring team',
  hard_boom: 'Hard boom',
  absorbent_boom: 'Absorbent boom',
  boom: 'Containment boom',
  foam: 'Foam supply',
  absorbent_pads: 'Absorbent pads',
  underflow_dam: 'Underflow dam',
  overflow_dam: 'Overflow dam',
  dike: 'Dike or berm',
  temporary_mats: 'Ground protection mats',
  restroom: 'Restrooms',
  porta_potty: 'Porta potty',
  frac_tank: 'Frac tank',
  vacuum_tanker: 'Tanker',
  vac_trucks: 'Vacuum truck',
  road: 'Temporary road, open or closed',
  valve_open: 'Valve shown open',
  valve_closed: 'Valve shown closed',
  pump: 'Pump station',
  compressor: 'Compressor',
  regulator_station: 'Regulator station',
  city_gate: 'City gate station',
  mercaptan_station: 'Mercaptan station',
  meter_station: 'Meter station',
  school: 'School',
  daycare: 'Daycare',
  hospital: 'Hospital',
  water_treatment: 'Water treatment plant',
}

export function paletteHint(typeId: string): string {
  return PALETTE_HINTS[typeId] ?? ''
}

export function paletteLabel(typeId: string): string {
  if (isBrwnPlumeType(typeId)) return brwnPlumeLabel()
  if (typeId === 'wind') return 'Wind'
  if (typeId === 'label') return 'Label'
  if (typeId === 'story_beat') return 'Story card'
  if (typeId === 'sketch') return 'Sketch'
  if (typeId === 'canal') return 'Canal'
  return ITEM_BY_ID.get(typeId)?.label ?? typeId
}

/** Auto-derived Timeline cards. Labels and wind stay off unless the author opts in. */
export function timelineDefaultVisible(typeId: string): boolean {
  return typeId !== 'label' && typeId !== 'wind' && typeId !== 'sketch'
}

export function objectShowsOnTimeline(typeId: string, showOnTimeline?: boolean): boolean {
  return showOnTimeline ?? timelineDefaultVisible(typeId)
}

/** Standard keyframe kinds an object type may expose in Inspect. Default: none. */
export type PaletteKeyframeKind = 'waypoints' | 'visibility' | 'status' | 'radius'

export type PaletteKeyframeCaps = Partial<Record<PaletteKeyframeKind, true>>

/**
 * Whitelist of keyframe editors per palette id. Types omitted here get no
 * Waypoints / Visibility / Status folds. Radius-over-time when frames already
 * exist (legacy) or a type opts into `radius`.
 *
 * Static hazards use Show / Hide (two clocks) instead of Visibility keyframes.
 */
const KEYFRAME_CAPS: Record<string, PaletteKeyframeCaps> = {
  // Moving apparatus
  car: { waypoints: true, visibility: true },
  truck: { waypoints: true, visibility: true },
  engine: { waypoints: true, visibility: true },
  ladder: { waypoints: true, visibility: true },
  ems: { waypoints: true, visibility: true },
  hazmat: { waypoints: true, visibility: true },
  rescue: { waypoints: true, visibility: true },
  rehab: { waypoints: true, visibility: true },
  police: { waypoints: true, visibility: true },
  work_vehicle: { waypoints: true, visibility: true },
  bulldozer: { waypoints: true, visibility: true },
  forestry_bulldozer: { waypoints: true, visibility: true },
  backhoe: { waypoints: true, visibility: true },
  boat: { waypoints: true, visibility: true },
  airplane: { waypoints: true, visibility: true },
  helicopter: { waypoints: true, visibility: true },
  drone: { waypoints: true, visibility: true },
  rollover_vehicle: { waypoints: true, visibility: true },
  contractor_response: { waypoints: true, visibility: true },
  // Weather track — position only (local wind is authored on the wind panel)
  tornado: { waypoints: true },
  hurricane: { waypoints: true },
  // Comedy footprint growth (easter egg)
  brwn_plume: { radius: true },
  // Command / logistics / locations — appear/hide mid-clock (hazards use Show / Hide only)
  icp: { visibility: true },
  dispatch: { visibility: true },
  pipeline_personnel: { visibility: true },
  responder: { visibility: true },
  incident_commander: { visibility: true },
  chief_officer: { visibility: true },
  staging: { visibility: true },
  media_staging: { visibility: true },
  muster: { visibility: true },
  access_point: { visibility: true },
  contractor: { visibility: true },
  operator: { visibility: true },
  person: { visibility: true },
  child: { visibility: true },
  air_monitoring: { visibility: true },
  hard_boom: { visibility: true },
  absorbent_boom: { visibility: true },
  boom: { visibility: true },
  foam: { visibility: true },
  absorbent_pads: { visibility: true },
  underflow_dam: { visibility: true },
  overflow_dam: { visibility: true },
  dike: { visibility: true },
  temporary_mats: { visibility: true },
  restroom: { visibility: true },
  porta_potty: { visibility: true },
  frac_tank: { visibility: true },
  vacuum_tanker: { visibility: true },
  vac_trucks: { visibility: true },
  valve_open: { visibility: true },
  valve_closed: { visibility: true },
  pump: { visibility: true },
  compressor: { visibility: true },
  regulator_station: { visibility: true },
  city_gate: { visibility: true },
  mercaptan_station: { visibility: true },
  meter_station: { visibility: true },
  school: { visibility: true },
  daycare: { visibility: true },
  hospital: { visibility: true },
  water_treatment: { visibility: true },
  // Roads schedule open / slow / closed (no waypoints / visibility)
  road: { status: true },
  drawbridge: { status: true },
}

export function paletteKeyframeCaps(typeId: string): PaletteKeyframeCaps {
  return KEYFRAME_CAPS[typeId] ?? {}
}

export function supportsKeyframe(typeId: string, kind: PaletteKeyframeKind): boolean {
  return paletteKeyframeCaps(typeId)[kind] === true
}
